<template>
  <div
    :key="tag.id"
    class="tagElementWrapper"
    :class="{ selected, readonly, error }"
    @click.stop="selectTag"
  >
    <div class="name ellipsis">
      {{ $t(`tags.${i18nkey}.${tag.name}`) }}
    </div>
    <transition v-if="!readonly" name="fade" mode="out-in">
      <i v-if="!selected" key="add" class="fas fa-plus" />
      <i v-else key="check" class="fas fa-check" />
    </transition>
  </div>
</template>

<script setup lang="ts">
import type Tag from '~/entities/tag'
import type TagTypeKey from '~/types/tagTypeKey'

type Props = {
  tag: Tag
  i18nkey: TagTypeKey
  selected?: boolean
  readonly?: boolean
  error?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  selected: false,
  readonly: false,
})

const emits = defineEmits(['click'])

function selectTag() {
  emits('click', props.tag, props.selected)
}
</script>

<style scoped lang="scss">
.tagElementWrapper {
  @apply tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-overflow-hidden tw-rounded-full tw-border tw-border-solid tw-border-gray-300 tw-bg-white tw-px-4 tw-py-2;

  &.readonly {
    @apply tw-cursor-default tw-border-transparent tw-bg-gray-500;
    --tw-bg-opacity: 0.05;
  }

  &.error {
    @apply tw-border-error;
  }
}

.name {
  @apply tw-text-xs tw-font-normal tw-text-gray-500;
}

.readonly .name {
  @apply tw-font-normal;
}

i,
.name,
.tagElementWrapper {
  @apply tw-transition tw-duration-150 tw-ease-in-out;
}

i {
  @apply tw-ml-2 tw-text-xs tw-text-gray-400;
  width: 11px;
}

.selected.tagElementWrapper {
  background: var(--color-main-5-p);
  border: 1px solid var(--color-main);
  box-shadow: var(--shadow-regular);
}

.selected i {
  background: linear-gradient(
    -45deg,
    rgb(255, 200, 87) 0%,
    rgb(235, 69, 124) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.selected .name {
  @apply tw-font-medium tw-text-orange-500;
}
</style>
